import { useEffect, useMemo, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { DateRangeField, useDateRange } from '@odo/shared-ui';
import type { FilterOptionItem } from '@odo/shared-ui';
import { usePracticeOptions } from '../../../hooks/useBilling';
import { practiceOptionsFromList } from '../../../utils/practiceFilterOptions';
import { fetchPatientsPage } from '../../../services/patientsV2';
import { exportInvoicesCsv } from '../../../services/billing';
import { apiErrorMessage } from '../../../utils/apiError';
import { downloadBlob } from '../../../utils/downloadBlob';
import { slugify } from '../../../utils/slugify';
import { SearchSelect } from '../SearchSelect';
import { useEscapeKey } from '../../../hooks/useEscapeKey';

// Export dialog: creation-date range (last 3 months by default), optional
// practice, optional patient. The CSV is built server-side - it spans the
// range, not the page the table holds, and carries columns the list lacks.

const PATIENT_SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_RANGE_MONTHS = 3;
// Free-text patient search across all practices only starts once the term has
// enough substance to be worth a request.
const MIN_PATIENT_SEARCH_LENGTH = 2;

const FIELD_LABEL_CLASS = 'block text-[13px] font-semibold text-gray-700 mb-1.5';

export interface ExportInvoicesModalProps {
    // Locks the practice on a practice-scoped (embedded) invoices pane.
    practiceId?: string;
    /** Prefills (but doesn't lock) the practice - the table's own filter. */
    initialPracticeId?: string;
    onClose: () => void;
}

export function ExportInvoicesModal({
    practiceId,
    initialPracticeId,
    onClose
}: ExportInvoicesModalProps) {
    const range = useDateRange({ defaultMonths: DEFAULT_RANGE_MONTHS });
    const { from, to, isInvalid: rangeInvalid } = range;
    const [selectedPracticeId, setSelectedPracticeId] = useState(
        practiceId ?? initialPracticeId ?? ''
    );
    const [selectedPatientId, setSelectedPatientId] = useState('');
    const [isExporting, setIsExporting] = useState(false);

    useEscapeKey(onClose, !isExporting);
    const [error, setError] = useState<string | null>(null);

    const { practices } = usePracticeOptions();
    const practiceOptions = useMemo(() => practiceOptionsFromList(practices), [practices]);
    const selectedPracticeName = practices.find((p) => p.id === selectedPracticeId)?.name;

    // Patients are searched server-side: scoped to the practice when one is
    // picked, otherwise across all practices once the search has substance.
    const [patientSearch, setPatientSearch] = useState('');
    const [debouncedPatientSearch, setDebouncedPatientSearch] = useState('');
    useEffect(() => {
        const handle = setTimeout(
            () => setDebouncedPatientSearch(patientSearch.trim()),
            PATIENT_SEARCH_DEBOUNCE_MS
        );
        return () => clearTimeout(handle);
    }, [patientSearch]);

    const patientQueryEnabled =
        Boolean(selectedPracticeId) || debouncedPatientSearch.length >= MIN_PATIENT_SEARCH_LENGTH;
    const { data: patientsData, isLoading: patientsLoading } = useQuery({
        queryKey: ['v2/patients', 'invoice-export', selectedPracticeId, debouncedPatientSearch],
        queryFn: () =>
            fetchPatientsPage({
                page: 1,
                limit: 20,
                sort: 'patient',
                order: 'asc',
                practiceIds: selectedPracticeId ? [selectedPracticeId] : undefined,
                search: debouncedPatientSearch || undefined
            }),
        enabled: patientQueryEnabled,
        staleTime: 60 * 1000
    });
    // Pinned by id+label: the options list only holds the fetched page, so a
    // search-found patient would look unselected once the search resets.
    const [pickedPatient, setPickedPatient] = useState<FilterOptionItem | null>(null);
    const patientOptions = useMemo<FilterOptionItem[]>(() => {
        const options: FilterOptionItem[] = (patientsData?.data ?? []).map((p) => ({
            value: p.id,
            label: p.name,
            sublabel: p.practiceName ?? undefined
        }));
        if (pickedPatient && !options.some((o) => o.value === pickedPatient.value)) {
            options.unshift(pickedPatient);
        }
        return options;
    }, [patientsData, pickedPatient]);

    const handlePatientChange = (id: string) => {
        setSelectedPatientId(id);
        setPickedPatient(patientOptions.find((o) => o.value === id) ?? null);
    };
    // A patient picked under one practice may not belong to the next one.
    const handlePracticeChange = (id: string) => {
        setSelectedPracticeId(id);
        setSelectedPatientId('');
        setPickedPatient(null);
        setPatientSearch('');
        setDebouncedPatientSearch('');
    };

    // Names, not ids, so a downloaded file is recognisable in a folder.
    const buildFilename = (): string =>
        [
            'invoices',
            selectedPracticeName ? slugify(selectedPracticeName) : null,
            pickedPatient ? slugify(pickedPatient.label) : null,
            from,
            to
        ]
            .filter(Boolean)
            .join('_') + '.csv';

    const handleExport = async () => {
        if (rangeInvalid || isExporting) return;
        setIsExporting(true);
        setError(null);
        try {
            const { blob, truncated } = await exportInvoicesCsv({
                from: from || undefined,
                to: to || undefined,
                practiceId: selectedPracticeId || undefined,
                patientId: selectedPatientId || undefined
            });
            downloadBlob(blob, buildFilename());
            // The file still downloads - the range just needs narrowing for
            // the rest, so the dialog stays open to say so.
            if (truncated) {
                setError('Too many invoices for one file. Narrow the range and export again.');
                return;
            }
            onClose();
        } catch (err) {
            setError(apiErrorMessage(err, 'Export failed. Please try again.'));
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-6">
            {/* No overflow-hidden: the pickers' menus are absolutely
                positioned and would be clipped at the card's edge. */}
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[560px] flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 shrink-0 flex items-start justify-between gap-4">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">Export invoices</h2>
                        <p className="text-[13px] text-gray-500 mt-0.5">
                            A CSV of invoices created in the selected period
                            {practiceId ? ' for this practice' : ''}.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors shrink-0"
                        aria-label="Close"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                <div className="p-6 flex flex-col gap-5">
                    <DateRangeField
                        range={range}
                        label="Created between"
                        hint={`Defaults to the last ${DEFAULT_RANGE_MONTHS} months.`}
                        idPrefix="export"
                    />

                    {/* Scope */}
                    <div className="grid grid-cols-2 gap-4">
                        {!practiceId && (
                            <div>
                                <span className={FIELD_LABEL_CLASS}>Practice (optional)</span>
                                <SearchSelect
                                    value={selectedPracticeId}
                                    options={practiceOptions}
                                    onChange={handlePracticeChange}
                                    placeholder="All practices"
                                    searchPlaceholder="Search practices..."
                                />
                            </div>
                        )}
                        <div>
                            <span className={FIELD_LABEL_CLASS}>Patient (optional)</span>
                            <SearchSelect
                                value={selectedPatientId}
                                options={patientOptions}
                                onChange={handlePatientChange}
                                onSearchChange={setPatientSearch}
                                placeholder="All patients"
                                searchPlaceholder="Search patients..."
                            />
                            <p className="mt-1 text-[11px] text-gray-400">
                                {patientQueryEnabled && patientsLoading
                                    ? 'Searching patients…'
                                    : selectedPracticeId
                                      ? 'Patients of the selected practice.'
                                      : 'Type at least 2 characters to search.'}
                            </p>
                        </div>
                    </div>

                    {error && <p className="text-sm text-red-500">{error}</p>}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 flex items-center gap-3 shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => void handleExport()}
                        disabled={isExporting || rangeInvalid}
                        className="ml-auto bg-black text-white rounded-xl px-6 py-2 text-sm font-semibold hover:bg-gray-900 transition-colors shadow disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isExporting ? 'Exporting…' : 'Export CSV'}
                    </button>
                </div>
            </div>
        </div>
    );
}
